// ClientApp/src/hooks/useApi.js
// ---------------------------------------------------------------------------
// Tiny fetch helpers (same pattern as Gandalf). The Vite dev server proxies
// /api → ASP.NET dev URL (configured in vite.config.js), so relative URLs
// work in both dev and production. Controllers return camelCase JSON.
// ---------------------------------------------------------------------------

async function request(path, init = {}) {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: { Accept: "application/json", ...(init.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    let body = null;
    const text = await res.text().catch(() => "");
    try { body = text ? JSON.parse(text) : null; } catch { body = null; }
    const err = new Error(`HTTP ${res.status} ${res.statusText} — ${text}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  get:  (path)       => request(path),
  post: (path, body) => request(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: body == null ? undefined : JSON.stringify(body) }),
};

// ----- Filter → query-string ------------------------------------------------
// filter = { campuses:[], categories:[], sources:[], vendors:[], businessEx:[], from:"", to:"" }
export function filterToQuery(filter = {}) {
  const p = new URLSearchParams();
  for (const c of filter.campuses   ?? []) p.append("campus", c);
  for (const c of filter.categories ?? []) p.append("category", c);
  for (const s of filter.sources    ?? []) p.append("source", s);
  for (const v of filter.vendors    ?? []) p.append("vendor", v);
  for (const b of filter.businessEx ?? []) p.append("businessEx", b);
  if (filter.from) p.set("from", filter.from);
  if (filter.to)   p.set("to", filter.to);
  return p.toString();
}

// ----- Per-page convenience wrappers ----------------------------------------

const withRun = (qs, runId) => (runId ? `${qs}${qs ? "&" : ""}runId=${runId}` : qs);

export const AnalyticsApi = {
  health:    ()                        => api.get("/api/health"),
  meta:      (runId)                   => api.get(`/api/meta?${withRun("", runId)}`),
  summary:   (filter, runId)           => api.get(`/api/summary?${withRun(filterToQuery(filter), runId)}`),
  breakdown: (groupBy, filter, runId)  => api.get(`/api/breakdown?groupBy=${encodeURIComponent(groupBy)}&${withRun(filterToQuery(filter), runId)}`),
};

export const AuthApi = {
  me:     () => api.get("/api/auth/me"),
  logout: () => api.post("/api/auth/logout"),
};

export const UsersApi = {
  list:   ()          => api.get("/api/admin/users"),
  create: (user)      => api.post("/api/admin/users", user),
  update: (id, user)  => request(`/api/admin/users/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(user) }),
  remove: (id)        => request(`/api/admin/users/${id}`, { method: "DELETE" }),
};

export const RunsApi = {
  list:    () => api.get("/api/runs"),
  current: () => api.get("/api/runs/current"),
};

export const PipelineApi = {
  sources:       ()                      => api.get("/api/admin/sources"),
  ingest:        (source)                => api.post(`/api/admin/ingest?source=${encodeURIComponent(source)}`),
  ingestRuns:    ()                      => api.get("/api/admin/ingest/runs"),
  preview:       (runId, table, limit)   => api.get(`/api/admin/runs/${runId}/preview?table=${table}&limit=${limit ?? 25}`),
  columns:       (runId, table)          => api.get(`/api/admin/runs/${runId}/columns?table=${table}`),
  qualityReport: (runId)                 => api.get(`/api/admin/runs/${runId}/quality-report`),
};
